<script lang="ts" setup>
import {
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  defineAsyncComponent,
} from "vue";
import StatusInternet from "~/components/common/StatusInternet.vue";
import CustomNotification from "~/components/common/CustomNotification.vue";
import WhatsAppButton from "~/components/common/WhatsAppButton.vue";
import type Customer from "./models/Customer";
import { useMounted } from "@vueuse/core";
import auth from "./services/AuthService";

const AccountStatus = defineAsyncComponent(
  () => import("@/components/common/AccountStatus.vue"),
);
const deviceStatus = defineAsyncComponent(
  () => import("@/components/common/DeviceStatus.vue"),
);
const verificationCode = defineAsyncComponent(
  () => import("@/components/common/VerificationCode.vue"),
);
const showNotification = ref(false);
const onLine = ref(typeof navigator === "object" && navigator.onLine);
const showBackOnline = ref(false);
const verifiedAccount = ref(false);

const route = useRoute();
const handleAccountUpdated = (data: Customer) => {
  if (data.email_verified_at) {
    verifiedAccount.value = true;
  }
};

onMounted(() => {
  window.addEventListener("online", updateOnlineStatus);
  window.addEventListener("offline", updateOnlineStatus);
});

onBeforeUnmount(() => {
  window.removeEventListener("online", updateOnlineStatus);
  window.removeEventListener("offline", updateOnlineStatus);
});

function updateOnlineStatus(e: Event) {
  showNotification.value = true;
  onLine.value = e.type === "online";
}

watch(onLine, (v) => {
  if (v) {
    showBackOnline.value = true;
    setTimeout(() => {
      showBackOnline.value = false;
      showNotification.value = false;
      window.location.href = route && route.fullPath;
    }, 2000);
  }
});

const mounted = useMounted();

// Limpia la sesión si el token actual ya no es válido.
if (!import.meta.env.SSR) {
  const isUserAuth = await auth.isCustomerTokenExpired();
  if (isUserAuth) {
    auth.clearSessionStorage();
  }
}
</script>

<template>
  <div>
    <verification-code />
    <device-status />
    <custom-notification v-if="mounted" />
    <div v-if="mounted" class="d-none d-lg-block">
      <account-status @account-verified-successfully="handleAccountUpdated" />
    </div>

    <div v-if="mounted && showNotification" class="d-none d-lg-block">
      <status-internet
        v-if="showNotification"
        :on-line="onLine"
        :show-back-online="showBackOnline"
      />
    </div>

    <NuxtLayout>
      <div
        v-if="mounted && showNotification"
        class="d-block d-lg-none fixed-top bg-dark"
      >
        <status-internet
          v-if="showNotification"
          :on-line="onLine"
          :show-back-online="showBackOnline"
        />
      </div>
      <NuxtPage />
    </NuxtLayout>
    <whats-app-button />
  </div>
</template>

<style lang="scss">
.resend-email:hover {
  cursor: pointer;
  padding: 1px;
  color: var(--bs-red);
}
</style>
